import React, { useCallback, useEffect, useState } from "react";
import { Dialog, Notification } from "@rio-cloud/rio-uikit";
import Button from "@rio-cloud/rio-uikit/Button";
import { DICT, forceHHMM, formatMaskTime } from "@utils";
import { CeckpointStepV2, CehckpointFieldsV2, CehckpointFieldV2, CheckpointV2 } from "src/types/checkpoint";
import { FileEntry, StepRender } from "./StepRender";
import ConfirmDialog from "../Dialogs/Confirm";
import { getTemplateCheckpointV2, getTemplatesCheckpointV2, updateCheckpointV2, uploadStepDocument } from "src/services/checkpoint";
import { hhmmToMinutes, minutesToHHMM, time2minutes } from "src/utils/time2minutes";

interface Props {
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  checkpoint: CheckpointV2;
}

const EditCheckpoint = ({ open, onClose, checkpoint }: Props) => {
  const [checkpointType, setCheckpointType] = useState("");

  const [title, setTitle] = useState("");
  const [estimatedTime, setEstimatedTime] = useState("");

  const [quantity, setQuantity] = useState(0);
  const [steps, setSteps] = useState<CeckpointStepV2[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [templates, setTemplates] = useState<CeckpointStepV2[]>([]);
  const [menu, setMenu] = useState<string[]>([]);
  const [stepFiles, setStepFiles] = useState<Record<string, FileEntry[]>>({});
  const [saving, setSaving] = useState(false);

  const init = useCallback(async () => {
    const response = await getTemplatesCheckpointV2();
    if (response.status === "success") {
      setTemplates(response.data.result);
      const itens = (response.data.result as CeckpointStepV2[])
        .map((s) => s.title)
        .filter((title): title is string => typeof title === "string");

      setMenu(itens);
    }
  }, []);

  useEffect(() => {
    if (open) {
      init();
      setStepFiles({});
    }
  }, [open, init]);

  useEffect(() => {
    const totalMinutes = steps.reduce((total, step) => {
      return total + time2minutes(step.estimatedTimeMinutes ?? "00:00");
    }, 0);

    const totalTime = minutesToHHMM(totalMinutes);
    setEstimatedTime(totalTime);
  }, [steps]);

  const handleDeleteStep = (i: number) => {
    setSteps((prevSteps) => prevSteps.filter((_, index) => index !== i));
  };

  const handleChangeCheckpointType = (checkpointType: string) => {
    setCheckpointType(checkpointType);
    setShowConfirmDialog(true);
  };

  const handleConfirmChangeCheckpointType = async () => {
    const selected = templates.filter((m) => m.title === checkpointType);
    if (selected) {
      setSteps(selected);
    }
    setShowConfirmDialog(false);
  };

  const resolveField = (field?: CehckpointFieldV2) => {
    if (!field) return undefined;

    if (field.id) {
      const matched = templates
        ?.flatMap((t) => t.fields || [])
        .find((f) => f.field?.id === field.id)?.field;
      return matched || field;
    }

    return field;
  };

  const buildFieldPayload = (field: CehckpointFieldsV2) => {
    const payload: any = {
      required: field.required ?? false,
      estimatedValue: field.estimatedValue === null || field.estimatedValue === undefined
        ? null
        : String(field.estimatedValue),
      field: resolveField(field.field),
    };

    if (field.id && field.id !== "") {
      payload.id = field.id;
    }

    if (field.optionValue !== undefined) {
      payload.optionValue = field.optionValue;
    }

    if (field.actualValue !== undefined && field.actualValue !== null) {
      payload.actualValue = field.actualValue;
    }

    return payload;
  };

  const handleUploadFiles = async (checkpointId: string, savedSteps: any[]) => {
    const hasFiles = Object.values(stepFiles).some((files) => files.length > 0);
    if (!hasFiles) return;

    for (let i = 0; i < savedSteps.length; i++) {
      const files = stepFiles[String(i)];
      if (!files || files.length === 0) continue;

      const step = savedSteps[i];
      const docField = step.fields?.find((f: any) => f.field?.name === "DOCUMENT" || f.field?.id === 19);
      if (!docField?.id) continue;

      for (const entry of files) {
        try {
          await uploadStepDocument(checkpointId, step.id, docField.id, entry.file);
        } catch (error) {
          console.error(`Erro ao enviar anexo "${entry.file.name}":`, error);
          Notification.error(`Erro ao enviar anexo "${entry.file.name}"`);
        }
      }
    }
  };

  const handleSubmit = async () => {
    setSaving(true);

    const key = Object.keys(DICT).find((k) => DICT[k as keyof typeof DICT] === checkpointType);

    const payload: any = {
      checkpointType: key,
      title,
      estimatedTimeMinutes: time2minutes(estimatedTime),
      quantity,
      steps: steps.map(({ estimatedTimeMinutes, ...step }) => ({
        ...step,
        estimatedTimeMinutes: time2minutes(estimatedTimeMinutes ?? ""),
        fields: step.fields?.map((field) => buildFieldPayload(field)),
      })),
    };

    try {
      const response = await updateCheckpointV2(checkpoint.id, payload);

      if (response.status !== "success") {
        Notification.error("Erro ao atualizar checklist");
        setSaving(false);
        return;
      }

      const updated = response.data?.result;

      // Atualiza os steps com os dados retornados (com IDs reais dos fields)
      if (updated?.steps) {
        setSteps(updated.steps.map((s: any) => ({
          ...s,
          estimatedTimeMinutes: s.estimatedTimeMinutes != null
            ? minutesToHHMM(s.estimatedTimeMinutes)
            : "00:00",
        })));
      }

      // Upload de anexos usando os IDs da response
      if (updated?.id && updated?.steps) {
        await handleUploadFiles(updated.id, updated.steps);
      }

      Notification.success("Checklist de parada atualizado com sucesso.");
      handleReset();
    } catch (error) {
      console.error("Erro ao atualizar checklist:", error);
      Notification.error("Erro ao atualizar checklist");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    onClose(false);
  };

  const addStep = async () => {
    const key = Object.keys(DICT).find((k) => DICT[k as keyof typeof DICT] === checkpointType);

    try {
      const response = await getTemplateCheckpointV2(key!);
      if (response.status === "success") {
        setSteps((prevSteps) => [...prevSteps, ...response.data.result]);
        return;
      }
      Notification.error("Erro ao adicionar etapa");
    } catch (error) {
      Notification.error("Erro ao adicionar etapa");
    }
  };

  const injectMissingFields = (checkpointSteps: CeckpointStepV2[], loadedTemplates: CeckpointStepV2[]): CeckpointStepV2[] => {
    if (!loadedTemplates.length) return checkpointSteps;

    return checkpointSteps.map((step) => {
      const templateStep = loadedTemplates.find((t) => t.title === step.title);
      if (!templateStep?.fields) return step;

      const existingFieldIds = new Set(step.fields?.map((f) => f.field?.id).filter(Boolean));
      const missingFields = templateStep.fields.filter(
        (f) => f.field?.id && !existingFieldIds.has(f.field.id)
      );

      if (missingFields.length === 0) return step;

      return {
        ...step,
        fields: [...(step.fields || []), ...missingFields.map((f) => ({ ...f, id: "" as string }))],
      };
    });
  };

  useEffect(() => {
    if (checkpoint && open) {
      const checkpointTypeValue = DICT[checkpoint.checkpointType as keyof typeof DICT] || checkpoint.checkpointType;
      setCheckpointType(checkpointTypeValue);
      setTitle(checkpoint.title);
      setEstimatedTime(minutesToHHMM(checkpoint.estimatedTimeMinutes));
      setQuantity(checkpoint.quantity);
      setSteps(checkpoint.steps || []);
    }
  }, [checkpoint, open]);

  useEffect(() => {
    if (templates.length > 0 && steps.length > 0) {
      const stepsWithFields = injectMissingFields(steps, templates);
      const needsUpdate = stepsWithFields.some((s, i) => s !== steps[i]);
      if (needsUpdate) {
        setSteps(stepsWithFields);
      }
    }
  }, [templates]);

  return (
    <>
      <Dialog
        show={open}
        onClose={() => handleReset()}
        onEsc={() => handleReset()}
        bsSize="xl"
        className="checkpointDialog"
        bodyClassName="padding-25"
        title="Selecionar/editar Checklist de parada"
        body={
          <div>
            <div className="lead">
              <div className="text-size-h6 text-bold">Dados do checklist</div>

              <div className="display-flex flex-column gap-24">
                <div className="display-flex flex-row gap-10">
                  <div className="flex-1-1 form-group">
                    <label form="checkpoint_type">Tipo de parada</label>
                    <select
                      disabled
                      className="form-control"
                      id="select"
                      value={checkpointType}
                      onChange={(e) => handleChangeCheckpointType(e.target.value)}
                    >
                      {menu.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1-1 form-group">
                    <label form="title">Título do checklist</label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="flex-1-1 form-group">
                    <label form="time">Tempo estimado do checklist</label>
                    <input
                      disabled
                      id="time"
                      type="text"
                      className="form-control"
                      min="00:00"
                      // max="23:59"
                      value={estimatedTime}
                      placeholder="00:00"
                    />
                  </div>
                  {checkpointType !== DICT.MECHANICAL_EMERGENCY && (
                    <div className="flex-1-1 form-group">
                      <label form="quantity">Quantidade (Unidades)</label>
                      <input
                        type="number"
                        className="form-control"
                        id="quantity"
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <hr />
            {steps.length > 0 ? (
              <StepRender steps={steps} deleteStep={handleDeleteStep} updateStep={setSteps} onFilesChange={setStepFiles} checkpointType={checkpointType} />
            ) : (
              <></>
            )}

            <Button iconName="rioglyph rioglyph-plus" onClick={addStep}>
              Adicionar Etapa
            </Button>
            <hr />
            <div className="display-flex justify-content-end">
              <Button className="btn btn-primary" disabled={saving} onClick={handleSubmit}>
                {saving ? "Salvando..." : "Salvar Checklist"}
              </Button>
            </div>
          </div>
        }
      ></Dialog>
      <ConfirmDialog
        show={showConfirmDialog}
        onClickConfirm={async () => await handleConfirmChangeCheckpointType()}
        onClickCancel={() => {}}
        title="Alterar Tipo de checklist de parada"
        content="Você tem certeza que deseja alterar o tipo de checklist? Isso pode afetar as etapas associadas."
        confirmButtonText="Sim, alterar"
        cancelButtonText="Não, manter"
      />
    </>
  );
};

export default EditCheckpoint;
